import React, { useState, useCallback, useRef } from 'react';
import { Upload, X, Star, StarOff, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { AuthenticatedImage } from './AuthenticatedImage';

export interface ProductImage {
  id: string;
  imageUrl: string;
  altText?: string;
  displayOrder: number;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

interface MultipleImageUploadProps {
  productId?: string;
  existingImages?: ProductImage[];
  onImagesChange?: (images: ProductImage[]) => void;
  onUpload?: (files: File[], setFirstAsDefault?: boolean) => Promise<ProductImage[]>;
  onSetDefault?: (imageId: string) => Promise<ProductImage>;
  onRemoveImage?: (imageId: string) => Promise<void>;
  onReorderImages?: (imageOrders: Array<{ imageId: string; displayOrder: number }>) => Promise<ProductImage[]>;
  maxImages?: number;
  maxFileSize?: number; // in MB
  allowedTypes?: string[];
  disabled?: boolean;
}

export const MultipleImageUpload: React.FC<MultipleImageUploadProps> = ({
  productId,
  existingImages = [],
  onImagesChange,
  onUpload,
  onSetDefault,
  onRemoveImage,
  onReorderImages,
  maxImages = 10,
  maxFileSize = 10,
  allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  disabled = false,
}) => {
  const [images, setImages] = useState<ProductImage[]>(existingImages);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update images when existingImages prop changes
  React.useEffect(() => {
    setImages(existingImages);
  }, [existingImages]);

  const validateFiles = (files: File[]): string | null => {
    if (files.length + images.length > maxImages) {
      return `Cannot upload more than ${maxImages} images total`;
    }

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        return `Invalid file type: ${file.type}. Allowed types: ${allowedTypes.join(', ')}`;
      }
      if (file.size > maxFileSize * 1024 * 1024) {
        return `File ${file.name} is too large. Maximum size: ${maxFileSize}MB`;
      }
    }

    return null;
  };

  const handleFileSelect = useCallback(async (files: File[]) => {
    if (disabled) return;

    const validationError = validateFiles(files);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const setFirstAsDefault = images.length === 0; // Set first image as default if no images exist
      
      if (onUpload) {
        const newImages = await onUpload(files, setFirstAsDefault);
        const updatedImages = [...images, ...newImages].sort((a, b) => a.displayOrder - b.displayOrder);
        setImages(updatedImages);
        onImagesChange?.(updatedImages);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      setError(error instanceof Error ? error.message : 'Failed to upload images');
    } finally {
      setUploading(false);
    }
  }, [images, onUpload, onImagesChange, disabled, maxImages, maxFileSize, allowedTypes]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    handleFileSelect(files);
  }, [handleFileSelect, disabled]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    handleFileSelect(files);
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFileSelect]);

  const handleSetDefault = useCallback(async (imageId: string) => {
    if (disabled || !onSetDefault) return;

    try {
      const updatedImage = await onSetDefault(imageId);
      const updatedImages = images.map(img => ({
        ...img,
        isDefault: img.id === imageId
      }));
      setImages(updatedImages);
      onImagesChange?.(updatedImages);
    } catch (error) {
      console.error('Error setting default image:', error);
      setError(error instanceof Error ? error.message : 'Failed to set default image');
    }
  }, [images, onSetDefault, onImagesChange, disabled]);

  const handleRemoveImage = useCallback(async (imageId: string) => {
    if (disabled || !onRemoveImage) return;

    try {
      await onRemoveImage(imageId);
      const updatedImages = images.filter(img => img.id !== imageId);
      setImages(updatedImages);
      onImagesChange?.(updatedImages);
    } catch (error) {
      console.error('Error removing image:', error);
      setError(error instanceof Error ? error.message : 'Failed to remove image');
    }
  }, [images, onRemoveImage, onImagesChange, disabled]);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleDragOverImage = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  }, [draggedIndex]);

  const handleDropOnImage = useCallback(async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex || disabled || !onReorderImages) {
      return;
    }

    try {
      const reorderedImages = [...images];
      const [draggedImage] = reorderedImages.splice(draggedIndex, 1);
      reorderedImages.splice(dropIndex, 0, draggedImage);

      // Update display orders
      const imageOrders = reorderedImages.map((img, index) => ({
        imageId: img.id,
        displayOrder: index
      }));

      const updatedImages = await onReorderImages(imageOrders);
      setImages(updatedImages.sort((a, b) => a.displayOrder - b.displayOrder));
      onImagesChange?.(updatedImages);
    } catch (error) {
      console.error('Error reordering images:', error);
      setError(error instanceof Error ? error.message : 'Failed to reorder images');
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [draggedIndex, images, onReorderImages, onImagesChange, disabled]);

  const defaultImage = images.find(img => img.isDefault);
  const nonDefaultImages = images.filter(img => !img.isDefault).sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Legacy Images Info */}
      {images.some(img => img.id.startsWith('fallback-')) && (
        <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-300">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium mb-1">Legacy Images Detected</p>
            <p>Some images are from the previous system and have limited functionality. Upload new images to enable full features like setting defaults and reordering.</p>
          </div>
        </div>
      )}

      {/* Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center transition-colors
          ${disabled 
            ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 cursor-not-allowed' 
            : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 cursor-pointer'
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={allowedTypes.join(',')}
          onChange={handleFileInputChange}
          disabled={disabled || uploading}
          className="hidden"
        />
        
        <div className="space-y-2">
          <Upload className={`w-8 h-8 mx-auto ${disabled ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'}`} />
          <div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || uploading}
              className={`
                text-sm font-medium
                ${disabled 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300'
                }
              `}
            >
              {uploading ? 'Uploading...' : 'Click to upload'}
            </button>
            <span className="text-gray-500 dark:text-gray-400 text-sm"> or drag and drop</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {allowedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ')} up to {maxFileSize}MB each
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {images.length}/{maxImages} images uploaded
          </p>
        </div>
      </div>

      {/* Images Grid */}
      {images.length > 0 && (
        <div className="space-y-4">
          {/* Default Image */}
          {defaultImage && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Default Image</h4>
              <div className="relative group">
                <div className="relative w-32 h-32 rounded-lg overflow-hidden border-2 border-yellow-400 dark:border-yellow-500">
                  <AuthenticatedImage
                    src={defaultImage.imageUrl}
                    alt={defaultImage.altText || 'Product image'}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  </div>
                  {/* Legacy image indicator */}
                  {defaultImage.id.startsWith('fallback-') && (
                    <div className="absolute bottom-2 left-2 bg-blue-500 text-white px-2 py-1 rounded text-xs font-medium">
                      Legacy
                    </div>
                  )}
                </div>
                {!disabled && (
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleRemoveImage(defaultImage.id)}
                      disabled={defaultImage.id.startsWith('fallback-')}
                      className={`p-1 rounded-full transition-colors ${
                        defaultImage.id.startsWith('fallback-')
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-red-500 text-white hover:bg-red-600'
                      }`}
                      title={defaultImage.id.startsWith('fallback-')
                        ? 'Upload new images to replace legacy images'
                        : 'Remove image'
                      }
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Other Images */}
          {nonDefaultImages.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Additional Images {nonDefaultImages.length > 0 && `(${nonDefaultImages.length})`}
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {nonDefaultImages.map((image, index) => (
                  <div
                    key={image.id}
                    draggable={!disabled}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOverImage(e, index)}
                    onDrop={(e) => handleDropOnImage(e, index)}
                    className={`
                      relative group cursor-move
                      ${draggedIndex === index ? 'opacity-50' : ''}
                      ${dragOverIndex === index ? 'ring-2 ring-blue-400' : ''}
                    `}
                  >
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                      <AuthenticatedImage
                        src={image.imageUrl}
                        alt={image.altText || 'Product image'}
                        className="w-full h-full object-cover"
                      />
                      {/* Legacy image indicator */}
                      {image.id.startsWith('fallback-') && (
                        <div className="absolute bottom-1 left-1 bg-blue-500 text-white px-1 py-0.5 rounded text-xs font-medium">
                          Legacy
                        </div>
                      )}
                    </div>
                    {!disabled && (
                      <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity space-y-1">
                        <button
                          onClick={() => handleSetDefault(image.id)}
                          disabled={image.id.startsWith('fallback-')}
                          className={`p-1 rounded-full transition-colors ${
                            image.id.startsWith('fallback-')
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-yellow-500 text-white hover:bg-yellow-600'
                          }`}
                          title={image.id.startsWith('fallback-')
                            ? 'Upload new images to set as default'
                            : 'Set as default'
                          }
                        >
                          <StarOff className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleRemoveImage(image.id)}
                          disabled={image.id.startsWith('fallback-')}
                          className={`p-1 rounded-full transition-colors ${
                            image.id.startsWith('fallback-')
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-red-500 text-white hover:bg-red-600'
                          }`}
                          title={image.id.startsWith('fallback-')
                            ? 'Upload new images to replace legacy images'
                            : 'Remove image'
                          }
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {images.length === 0 && (
        <div className="text-center py-8">
          <ImageIcon className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-2" />
          <p className="text-gray-500 dark:text-gray-400 text-sm">No images uploaded yet</p>
        </div>
      )}
    </div>
  );
};